import React from "react";
import FilterSection from "../components/Filter";
import ProductList from "../components/ProductList";
import "../styles/category.css";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import { fetchFilteredProducts } from "../actions/FilterActions";
import { fetchCategories } from "../actions/categoriesActions";

// Recursive function to find a category by ID in nested structure
const findCategoryById = (categoryList, targetId) => {
  if (!categoryList || !Array.isArray(categoryList)) return null;

  // Convert both to strings for comparison to handle type mismatches
  const targetIdStr = String(targetId);

  for (const category of categoryList) {
    const categoryIdStr = String(category.category_id);
    if (categoryIdStr === targetIdStr) {
      return category;
    }
    if (
      category.children &&
      Array.isArray(category.children) &&
      category.children.length > 0
    ) {
      const found = findCategoryById(category.children, targetId);
      if (found) return found;
    }
  }
  return null;
};

// Recursive function to collect all nested category IDs
// This function collects ONLY the category itself and its nested descendants
// It does NOT include sibling categories
// Behavior:
//   - Parent category selected → returns [parent_id, child1_id, child2_id, grandchild1_id, ...]
//   - Subcategory selected → returns [subcategory_id, grandchild1_id, grandchild2_id, ...]
//     (does NOT include sibling subcategories)
const collectAllCategoryIds = (category) => {
  const ids = [category.category_id];

  if (
    category.children &&
    Array.isArray(category.children) &&
    category.children.length > 0
  ) {
    category.children.forEach((child) => {
      ids.push(...collectAllCategoryIds(child));
    });
  }

  return ids;
};

function Category() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, filteredProducts, count } = useSelector(
    (state) => state.filters,
  );
  const { categories } = useSelector((state) => state.categories);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(24);
  const [totalPages, setTotalPages] = useState(
    Math.ceil(count / productsPerPage),
  );
  const category_id = useParams().categoryId;
  const [allCategories, setAllCategories] = useState([]);
  const [filters, setFilters] = useState({
    category_id: [],
    min_price: 0,
    max_price: 10000000,
    skin_concern: [],
    skin_type: [],
    fomulation: [],
    keywords: [],
    brand: "",
    features: [],
    tags: [],
    search: "",
    status: "published",
  });

  useEffect(() => {
    // Only set filters from URL category_id if it exists
    if (category_id) {
      // Wait for categories to load
      if (!categories || categories.length === 0) {
        return;
      }

      // Find the category recursively in the nested structure
      const category = findCategoryById(categories, category_id);

      if (category) {
        // collectAllCategoryIds collects ONLY the selected category and its nested descendants
        // It does NOT include sibling categories, so:
        // - If selecting a parent category → shows parent + all its nested children
        // - If selecting a subcategory → shows ONLY that subcategory + its nested children (not siblings)
        const allCategoryIds = collectAllCategoryIds(category);

        setFilters((prevFilters) => ({
          ...prevFilters,
          category_id: allCategoryIds,
        }));

        // Reset to first page when category changes
        setCurrentPage(1);
      } else {
        // If category not found, still filter by the category_id
        setFilters((prevFilters) => ({
          ...prevFilters,
          category_id: [category_id],
        }));
        setCurrentPage(1);
      }
    }
    // If no category_id in URL, don't override filters (let user selections persist)
  }, [category_id, categories]);

  useEffect(() => {
    // Fetch products - empty category_id array means "all products"
    // Ensure we're sending the correct filter data
    const filterPayload = {
      ...filters,
      category_id: Array.isArray(filters.category_id)
        ? filters.category_id
        : [], // Explicitly include category_id array (empty = all products)
      page: currentPage,
      pagination: productsPerPage,
      status: filters.status || "published",
    };

    dispatch(fetchFilteredProducts(filterPayload));
  }, [filters, currentPage, productsPerPage, dispatch]);

  useEffect(() => {
    // Fetch categories on mount
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    // Update total pages when count changes
    setTotalPages(Math.ceil(count / productsPerPage));
  }, [count, productsPerPage]);

  useEffect(() => {
    // Update allCategories when category_id or categories change
    if (category_id && categories && categories.length > 0) {
      const category = findCategoryById(categories, category_id);
      if (category) {
        setAllCategories([category]);
      } else {
        setAllCategories([]);
      }
    } else {
      setAllCategories([]);
    }
  }, [category_id, categories]);

  useEffect(() => {
    // Scroll to top when category changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [category_id]);

  // Remove category_id from URL when all categories are unselected
  useEffect(() => {
    // If filters.category_id is empty and there's a category_id in the URL, navigate to remove it
    // Only navigate if the user manually unselected all categories (not when URL sets it)
    if (
      category_id &&
      (!filters.category_id ||
        !Array.isArray(filters.category_id) ||
        filters.category_id.length === 0)
    ) {
      // Navigate to search page when all categories are unselected
      navigate("/search", { replace: true });
    }
  }, [filters.category_id, category_id, navigate]);

  return (
    <div className="category-container">
      <div className="category-content">
        <FilterSection
          category_id={category_id}
          isSearchPage={false}
          filters={filters}
          setFilters={setFilters}
        />
        <ProductList
          categoryId={category_id}
          filters={filters}
          currentPage={currentPage}
          totalPages={totalPages}
          productsPerPage={productsPerPage}
          setCurrentPage={setCurrentPage}
          filteredProducts={filteredProducts}
          loading={loading}
        />
      </div>
    </div>
  );
}

export default Category;
